import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useScrollRestoration } from '@/lib/scrollRestoration';
import type { Email, SearchResult, Thread, Account } from '@/types/email';
import type { SearchResultItem } from '@/stores/searchStore';
import type { CalendarEventSearchResult } from '@/api/search';
import type { SearchToken } from '@/lib/search';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Hint } from '@/components/Hint';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Loader2, Paperclip, CheckCircle, Circle, Calendar, MapPin, Video, Search, ShieldOff, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getBrandfetchLogoUrl, resolveAvatarUrl } from '@/lib/avatar';

interface SearchResultsViewProps {
  results: SearchResultItem[];
  /** Tasks / events that also matched — shown as a compact block above the mail list */
  related?: SearchResultItem[];
  loading: boolean;
  error?: string | null;
  /** Parsed query tokens; filter tokens render as removable chips */
  tokens?: SearchToken[];
  onRemoveToken?: (tokenId: string) => void;
  /** Infinite scroll: true when the last page came back full */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  selectedEmailId?: string | null;
  selectedThreadId?: string | null;
  onSelectEmail: (email: Email) => void;
  onSelectThread: (thread: Thread) => void;
  onSelectTask?: (taskId: string) => void;
  onSelectCalendarEvent?: (event: CalendarEventSearchResult['calendar_event']) => void;
  query?: string;
  onQueryChange?: (query: string) => void;
  onSearchSubmit?: (query: string) => void;
  /** Blocked-folder mail is excluded server-side unless this is on */
  includeBlocked?: boolean;
  onIncludeBlockedChange?: (value: boolean) => void;
  onClose?: () => void;
  accountColors?: Record<string, string | null | undefined>;
  accounts?: Account[];
}

const stripHtmlCache = new Map<string, string>();
const MAX_STRIP_HTML_CACHE = 200;

function stripHtml(value: string): string {
  const cacheKey = value.length > 200 ? value.slice(0, 200) : value;
  const cached = stripHtmlCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(value, 'text/html');
  const text = doc.body.textContent || '';
  if (stripHtmlCache.size >= MAX_STRIP_HTML_CACHE) {
    const firstKey = stripHtmlCache.keys().next().value!;
    stripHtmlCache.delete(firstKey);
  }
  stripHtmlCache.set(cacheKey, text);
  return text;
}

function formatEmailDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const emailDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  if (emailDate.getTime() === today.getTime()) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } else if (emailDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else if (now.getTime() - d.getTime() < 7 * 24 * 60 * 60 * 1000) {
    return d.toLocaleDateString('en-US', { weekday: 'short' });
  } else if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}

/**
 * Render server highlight fragments, preserving <mark> emphasis as real
 * elements. Everything between the marks is treated as text (stripHtml also
 * decodes entities), so no document HTML ever reaches the DOM unescaped.
 */
function getHighlight(result: SearchResult): ReactNode | null {
  if (!result.highlight || result.highlight.length === 0) {
    return null;
  }
  const fragments = result.highlight.flatMap((fragment) => fragment.fragments);
  if (!fragments.length) {
    return null;
  }
  return fragments.map((fragment, fragmentIndex) => (
    <span key={fragmentIndex}>
      {fragmentIndex > 0 && ' … '}
      {fragment.split(/<\/?mark>/).map((part, partIndex) =>
        partIndex % 2 === 1 ? (
          <mark key={partIndex} className="rounded-sm bg-primary/15 px-0.5 font-medium text-foreground">
            {stripHtml(part)}
          </mark>
        ) : (
          stripHtml(part)
        )
      )}
    </span>
  ));
}

function getInitials(name?: string | null, email?: string) {
  if (name) {
    const initials = name
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0]?.toUpperCase())
      .join('');
    if (initials) {
      return initials.slice(0, 2);
    }
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return 'SR';
}

function formatCalendarEventDate(event: CalendarEventSearchResult['calendar_event']): string {
  const startDate = event.startDateTime || event.startDate;
  if (!startDate) return '';

  const date = new Date(startDate);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (event.allDay) {
    if (eventDate.getTime() === today.getTime()) {
      return 'Today (All day)';
    }
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

  if (eventDate.getTime() === today.getTime()) {
    return `Today, ${timeStr}`;
  }

  const dayStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return `${dayStr}, ${timeStr}`;
}

export function SearchResultsView({ results, related, loading, error, tokens, onRemoveToken, hasMore, loadingMore, onLoadMore, selectedEmailId, selectedThreadId, onSelectEmail, onSelectThread, onSelectTask, onSelectCalendarEvent, query, onQueryChange, onSearchSubmit, onClose, accountColors, accounts, includeBlocked, onIncludeBlockedChange }: SearchResultsViewProps) {
  const [localQuery, setLocalQuery] = useState(query ?? '');

  // Get account emails for sent detection
  const accountEmails = new Set((accounts || []).map((account) => account.emailAddress.toLowerCase()));

  // Keep the input in sync when the query changes outside it (chip removal,
  // URL-driven search). Typing only touches localQuery, so this fires on
  // committed query changes, not keystrokes.
  useEffect(() => {
    setLocalQuery(query ?? '');
  }, [query]);

  const handleQueryChange = useCallback((value: string) => {
    setLocalQuery(value);
    onQueryChange?.(value);
  }, [onQueryChange]);

  // Infinite scroll: sentinel at the list tail observed against the
  // ScrollArea viewport (same pattern as SenderDigestView).
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const pendingLoadRef = useRef(false);
  // Results for one query are one list: opening a hit and coming back lands
  // on the same row; a new query starts at the top.
  useScrollRestoration({
    scopeKey: `search:${(query ?? '').trim()}`,
    viewportRef,
    ready: !loading,
  });
  const canLoadMore = typeof onLoadMore === 'function' && Boolean(hasMore);

  useEffect(() => {
    if (!loadingMore) {
      pendingLoadRef.current = false;
    }
  }, [loadingMore]);

  useEffect(() => {
    if (!canLoadMore) {
      return;
    }
    const viewport = viewportRef.current;
    const sentinel = loadMoreRef.current;
    if (!viewport || !sentinel) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || pendingLoadRef.current) {
          return;
        }
        pendingLoadRef.current = true;
        onLoadMore?.();
      },
      { root: viewport, threshold: 0, rootMargin: '400px 0px 400px 0px' },
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [canLoadMore, onLoadMore]);

  const filterTokens = (tokens ?? []).filter((token) => token.type === 'filter');

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSearchSubmit?.(localQuery);
  }, [localQuery, onSearchSubmit]);

  const relatedResults = related ?? [];

  const showSearchInput = Boolean(onQueryChange || onSearchSubmit);
  const showResultCount = !loading && results.length > 0;
  // Deliberately NOT gated on results.length: a search whose only matches live
  // in Blocked comes back empty, and the switch is how the user gets to them.
  const showBlockedSwitch = Boolean(onIncludeBlockedChange);

  const renderResult = (result: SearchResultItem, index: number): ReactNode => {
            const delayIndex = Math.min(index, 12);
            const animationDelay = `${delayIndex * 45}ms`;
            switch (result.kind) {
              case 'thread': {
                const highlight = getHighlight(result);
                const thread = result.thread;
                const selected = selectedThreadId === thread.id;
                const firstParticipant = thread.participants[0];
                const avatarSrc = resolveAvatarUrl({
                  email: firstParticipant?.email,
                  avatarUrl: firstParticipant?.avatarUrl,
                });
                const snippet = highlight || thread.snippet;

                // Get account color for ring
                const accountColor = accountColors?.[thread.accountId] || null;
                const ringColor = accountColor || '#2563eb';
                const showRing = (thread.unreadCount ?? 0) > 0 && Boolean(accountColor);
                // Keyboard shortcuts hit-test rows by message id (F15) — anchor
                // the thread row to its newest message so e/u/Delete have a
                // real target instead of falling through to the hidden list.
                const newestMessage = [...(thread.messages ?? [])].sort(
                  (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                )[0];

                return (
                  <div
                    key={`thread-${thread.id}`}
                    data-scroll-key={`thread-${thread.id}`}
                    data-email-row-id={newestMessage?.id}
                    className={cn(
                      'flex cursor-pointer gap-4 rounded-xl p-4 transition-shadow hover:shadow-sm glass animate-fade-in-up',
                      selected && 'border-primary bg-primary/5 shadow-sm',
                    )}
                    onClick={() => onSelectThread(thread)}
                    style={{ animationDelay }}
                  >
                    <Avatar 
                      className="h-11 w-11"
                      style={showRing ? { 
                        boxShadow: `0 0 0 2px ${ringColor}`,
                      } : undefined}
                    >
                      {avatarSrc && (
                        <AvatarImage
                          src={avatarSrc}
                          alt={firstParticipant?.name || firstParticipant?.email || 'Thread participant avatar'}
                        />
                      )}
                      <AvatarFallback>
                        {getInitials(firstParticipant?.name, firstParticipant?.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className={cn(
                            "truncate text-sm",
                            (thread.unreadCount ?? 0) > 0 ? "font-bold" : "font-semibold"
                          )}>
                            {thread.subject}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className="text-xs">{thread.messageCount} msgs</Badge>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatEmailDate(thread.lastMessageAt)}
                          </span>
                        </div>
                      </div>
                      <p className={cn(
                        "truncate text-xs text-muted-foreground",
                        (thread.unreadCount ?? 0) > 0 && "font-medium"
                      )}>
                        {thread.participants.map((participant) => participant.name || participant.email).join(', ')}
                      </p>
                      {snippet && (
                        <p className="line-clamp-2 text-xs text-muted-foreground leading-relaxed">{snippet}</p>
                      )}
                    </div>
                  </div>
                );
              }
              case 'contact':
                // The backend never emits contact results; the schema union
                // still allows them, so skip rather than crash if one appears.
                return null;
              case 'task': {
                const task = result.task;
                const isCompleted = task.status === 'completed';
                const TaskIcon = isCompleted ? CheckCircle : Circle;

                return (
                  <div
                    key={`task-${task.id}`}
                    data-scroll-key={`task-${task.id}`}
                    className="flex items-center gap-4 rounded-xl p-4 glass animate-fade-in-up hover:shadow-sm transition-shadow cursor-pointer"
                    style={{ animationDelay }}
                    onClick={() => onSelectTask?.(task.id)}
                  >
                    <div className={cn(
                      "flex h-12 w-12 items-center justify-center rounded-full",
                      isCompleted ? "bg-success-surface text-success-ink" : "bg-info-surface text-info-ink"
                    )}>
                      <TaskIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-sm font-semibold">{task.title}</p>
                          {task.notesMarkdown && (
                            <p className="line-clamp-2 text-xs text-muted-foreground mt-1">
                              {task.notesMarkdown}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {task.priority && (
                            <Badge variant="secondary" className="text-xs">
                              {task.priority}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              case 'calendar_event': {
                const event = result.calendar_event;
                const hasConference = Boolean(event.conferenceData);

                return (
                  <div
                    key={`calendar-event-${event.id}`}
                    data-scroll-key={`calendar-event-${event.id}`}
                    className="flex items-center gap-4 rounded-xl p-4 glass animate-fade-in-up hover:shadow-sm transition-shadow cursor-pointer"
                    style={{ animationDelay }}
                    onClick={() => onSelectCalendarEvent?.(event)}
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent2-surface text-accent2-ink">
                      <Calendar className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-sm font-semibold">{event.summary || '(No title)'}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {formatCalendarEventDate(event)}
                          </p>
                          {event.location && (
                            <p className="flex items-center gap-1 truncate text-xs text-muted-foreground mt-1">
                              <MapPin className="h-3 w-3 shrink-0" />
                              {event.location}
                            </p>
                          )}
                          {event.description && (
                            <p className="line-clamp-1 text-xs text-muted-foreground mt-1">
                              {event.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {hasConference && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Video className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>Video conference</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              case 'email':
              default: {
                const email = result.email;
                const highlight = getHighlight(result);
                const selected = selectedEmailId === email.id;
                const threadCount = 'threadCount' in result && typeof result.threadCount === 'number' ? result.threadCount : 1;

                // Determine if this is a sent email
                const isSentEmail = email.from?.email
                  ? accountEmails.has(email.from.email.toLowerCase())
                  : false;

                // Get display info based on sent/received
                const displayPerson = isSentEmail ? email.to[0] : email.from;
                const displayEmail = displayPerson?.email || email.from.email;
                const displayName = displayPerson?.name || displayPerson?.email || email.from.email;

                const avatarSrc = getBrandfetchLogoUrl(displayEmail);
                const snippet = highlight || email.snippet || stripHtml(email.body).slice(0, 120);
                const hasAttachments = Boolean(
                  email.attachments && email.attachments.some((attachment) => !attachment.inline)
                );

                // Get account color for ring
                const accountColor = accountColors?.[email.accountId] || null;
                const ringColor = accountColor || '#2563eb';
                const showRing = !email.read && Boolean(accountColor);

                return (
                  <div
                    key={`email-${email.id}`}
                    data-scroll-key={`email-${email.id}`}
                    // Keyboard shortcuts hit-test this attribute so search rows
                    // are targetable (the hidden mailbox list never is).
                    data-email-row-id={email.id}
                    className={cn(
                      'flex cursor-pointer gap-4 rounded-xl p-4 hover:shadow-sm transition-shadow glass animate-fade-in-up',
                      selected && 'border-primary bg-primary/5 shadow-sm',
                    )}
                    onClick={() => onSelectEmail(email)}
                    style={{ animationDelay }}
                  >
                    <Avatar 
                      className="h-11 w-11"
                      style={showRing ? { 
                        boxShadow: `0 0 0 2px ${ringColor}`,
                      } : undefined}
                    >
                      {avatarSrc && (
                        <AvatarImage
                          src={avatarSrc}
                          alt={displayName}
                        />
                      )}
                      <AvatarFallback>{getInitials(displayPerson?.name, displayEmail)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className={cn(
                            "truncate text-sm",
                            !email.read ? "font-bold" : "font-semibold"
                          )}>
                            {email.subject}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {threadCount > 1 && (
                            <Badge variant="outline" className="text-xs">{threadCount} msgs</Badge>
                          )}
                          {hasAttachments && (
                            <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatEmailDate(email.date)}
                          </span>
                        </div>
                      </div>
                      <p className={cn(
                        "truncate text-xs text-muted-foreground",
                        !email.read && "font-medium"
                      )}>
                        {isSentEmail && <span className="text-muted-foreground/70">To: </span>}
                        {displayName}
                      </p>
                      {snippet && (
                        <p className="line-clamp-2 text-xs text-muted-foreground leading-relaxed">
                          {snippet}
                        </p>
                      )}
                    </div>

                  </div>
                );
              }
            }
            };

  return (
    <div className="flex h-full flex-col overflow-hidden glass-page animate-fade-in-up">
      <div className="shrink-0 glass subtle-divider flex flex-col gap-2 px-4 py-3">
        {showSearchInput && (
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <div className="relative flex-1">
              <Input
                placeholder="Search..."
                value={localQuery}
                onChange={(e) => handleQueryChange(e.target.value)}
                className="pr-10 bg-muted/20 border-transparent focus:bg-background transition-all duration-200 focus:ring-2 ring-primary/20 focus:border-primary/30"
              />
              <Button
                type="submit"
                size="icon"
                variant="ghost"
                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-primary"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
            {onClose && (
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Clear
              </Button>
            )}
          </form>
        )}
        {!showSearchInput && (
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <h2 className="text-sm font-medium text-muted-foreground">Search results</h2>
              {query && (
                <span className="text-xs text-muted-foreground">
                  for "{query}"
                </span>
              )}
            </div>
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>
                Clear search
              </Button>
            )}
          </div>
        )}
        {/* Result-type filters + the blocked-mail switch */}
        {(showResultCount || showBlockedSwitch) && (
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
            <div className="text-xs text-muted-foreground">
              {showResultCount && (
                <span>
                  {results.length}{hasMore ? '+' : ''} {results.length === 1 ? 'thread' : 'threads'}
                </span>
              )}
            </div>
            {showBlockedSwitch && (
              <Hint content="Mail from blocked senders lives in the Blocked folder and is left out of search by default. Turn this on to search it too (in:blocked does the same for one query).">
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                  <Switch
                    checked={Boolean(includeBlocked)}
                    onCheckedChange={(checked) => onIncludeBlockedChange?.(checked)}
                  />
                  <ShieldOff className="h-3 w-3" />
                  Blocked
                </label>
              </Hint>
            )}
          </div>
        )}
        {/* Removable filter chips (from:, in:, is:, …) */}
        {filterTokens.length > 0 && onRemoveToken && (
          <div className="flex flex-wrap gap-1.5">
            {filterTokens.map((token) => (
              <Badge key={token.id} variant="secondary" className="gap-1 pr-1 text-xs font-normal">
                <span className="text-muted-foreground">{token.key}:</span>
                {token.value}
                <button
                  type="button"
                  onClick={() => onRemoveToken(token.id)}
                  className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-muted-foreground/20"
                  aria-label={`Remove ${token.key}:${token.value} filter`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
      <ScrollArea className="flex-1" viewportRef={viewportRef}>
        <div className="space-y-3 px-4 py-4">
          {loading && (
            <div className="flex justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}
          {!loading && error && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
              {error}
            </div>
          )}
          {!loading && !error && results.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No results found. Try refining your search.
              {showBlockedSwitch && !includeBlocked && (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={() => onIncludeBlockedChange?.(true)}
                    className="underline underline-offset-2 hover:text-foreground"
                  >
                    Search blocked mail too
                  </button>
                  .
                </>
              )}
            </div>
          )}
          {relatedResults.length > 0 && (
            <div className="space-y-2 rounded-xl border border-border/40 bg-muted/20 p-2">
              <p className="px-2 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Also matched</p>
              {relatedResults.map(renderResult)}
            </div>
          )}
          {results.map(renderResult)}
          {!loading && !error && canLoadMore && (
            <div ref={loadMoreRef} className="flex justify-center py-3">
              {loadingMore && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
